import { motion, useScroll, useTransform } from "framer-motion";
import { useRef, useState, useEffect } from "react";
import { useIsMobile } from "@/hooks/use-mobile";
import logo from "@/assets/logo.webp";

const HeroSection = () => {
  const ref = useRef<HTMLDivElement>(null);
  const [videoLoaded, setVideoLoaded] = useState(false);
  const isMobile = useIsMobile();
  
  useEffect(() => {
    const timer = setTimeout(() => setVideoLoaded(true), 100);
    return () => clearTimeout(timer);
  }, []);
  
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"]
  });
  
  const y = useTransform(scrollYProgress, [0, 1], isMobile ? ["0%", "0%"] : ["0%", "30%"]);
  const scale = useTransform(scrollYProgress, [0, 1], isMobile ? [1, 1] : [1, 1.1]);
  const opacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  return (
    <section ref={ref} className="relative min-h-screen flex items-center justify-center overflow-hidden bg-background">
      {/* Video Background */}
      <div className="absolute inset-0 z-0">
        {videoLoaded && (
          <motion.div className="absolute inset-0" style={{ y, scale }}>
            <video
              autoPlay
              loop
              muted
              playsInline
              preload="none"
              aria-hidden="true"
              className="absolute inset-0 w-full h-full object-cover opacity-0 transition-opacity duration-1000"
              onCanPlay={(e) => {
                (e.target as HTMLVideoElement).classList.replace('opacity-0', 'opacity-100');
              }}
            >
              <source src={isMobile ? "/videos/hero-mobile.webm" : "/videos/hero-bg.webm"} type="video/webm" />
              <track kind="captions" />
            </video>
          </motion.div>
        )}
      </div>

      {/* Overlays */}
      <div className="absolute inset-0 z-[1] bg-background/60" />
      <div className="absolute inset-0 z-[2] bg-gradient-to-t from-background via-transparent to-background/40" />

      {/* Centered Content - logo rendered without motion wrapper to avoid LCP render delay */}
      <motion.div 
        style={{ opacity }}
        className="relative z-10 flex flex-col items-center text-center px-6 max-w-4xl mx-auto w-full"
      >
        <div className="flex flex-col items-center">
          <h1 className="sr-only" aria-hidden="false">Studio Skandal — przestrzeń eventowa w centrum Warszawy</h1>
          <img
            src={logo}
            alt="Studio Skandal"
            loading="eager"
            decoding="sync"
            fetchPriority="high"
            className="w-[85vw] sm:w-[70vw] md:w-[55vw] lg:w-[45vw] max-w-3xl h-auto"
            width="720"
            height="180"
          />
        </div>
        <p className="mt-8 text-xs sm:text-sm md:text-base text-white/40 font-light uppercase tracking-[0.25em] animate-page-fade-in">
          Twoja przestrzeń eventowa w centrum Warszawy
        </p>

        {/* Scroll Indicator - Golden Glowing Dot */}
        <div 
          className="mt-10 flex flex-col items-center cursor-pointer" 
          onClick={() => {
            document.getElementById('o-studio')?.scrollIntoView({ behavior: 'smooth' });
          }}
        >
          <div className="flex flex-col items-center">
            <div 
              className="w-[8px] h-[8px] rounded-full bg-primary animate-scroll-dot-fall"
              style={{ boxShadow: '0 0 12px 4px hsl(38 40% 60% / 0.6), 0 0 24px 8px hsl(38 40% 60% / 0.3)' }}
            />
          </div>
        </div>
      </motion.div>
    </section>
  );
};

export default HeroSection;
